import {
    handleCancelled,
    handleError,
    handleFileUpload,
    handleFileUploadComplete,
    handleProgress,
    removeUpload,
    setCallbackMessages,
} from 'common/store/fileUpload/actions';
import { ProgressPayload } from 'common/store/fileUpload/reducer';
import UploadService, { ErrorTypes, TicketData } from 'common/services/upload/UploadService';
import store from '../../store';
import { RootState } from '../../store/rootReducer';

const getState = () => (store.getState() as RootState).fileUpload;

export const supportedFileExtensions = ['*'];

export const fileUploadService = {
    handleCallback: (type: string, file: File, payload: any, id: any) => {
        const state = getState();
        const callbackMessages = state.callbackMessages.concat({
            type, file, payload, id,
        });
        store.dispatch(setCallbackMessages(callbackMessages));

        if (type === 'progress') {
            fileUploadService.handleProgress(payload);
        } else if (type === 'complete') {
            store.dispatch(handleFileUploadComplete(payload));
        } else if (type === 'cancelled') {
            store.dispatch(handleCancelled());
        } else if (type === 'error') {
            store.dispatch(handleError(ErrorTypes.UPLOAD_ERROR));
        }
    },
    handleProgress: (progress: any) => {
        const state = getState();
        const uploadProgressTime: number = new Date().getTime();
        const uploadProgress = parseFloat(progress);
        let uploadProgressEstimatedMsRemaining;
        const data: ProgressPayload = { uploadProgress, uploadProgressTime, uploadProgressEstimatedMsRemaining };

        if (uploadProgress !== 0 && state.uploadStartedAt) {
            uploadProgressEstimatedMsRemaining = fileUploadService.estimateTimeLeft(
                uploadProgress,
                state.uploadStartedAt.getTime(),
                uploadProgressTime,
            );
        }

        data.uploadProgressEstimatedMsRemaining = uploadProgressEstimatedMsRemaining;
        store.dispatch(handleProgress(data));
    },
    getFileDownloadUrl(contentItemId: number) {
        return `/file/ci/${contentItemId}/file`;
    },
    estimateTimeLeft: (progress: number, startTime: number, currentTime: number) => {
        const msTaken = currentTime - startTime;
        const partsPerMs = progress / msTaken;
        const remainingMs = Math.ceil((1 - progress) / partsPerMs);
        return remainingMs;
    },
    verifyFileType: (file: File) => {
        if (supportedFileExtensions.indexOf('*') > -1) {
            return true;
        }
        if (!file.name.includes('.')) {
            return false;
        }

        let fileExtension = file.name.split('.').pop().toLowerCase();
        fileExtension = `.${fileExtension}`;
        if (supportedFileExtensions.includes(fileExtension)) {
            return true;
        }
        return false;
    },
    removeUpload: () => {
        store.dispatch(removeUpload());
    },
    uploadFile: async (file: File, ticket: TicketData, csrfToken: string) => {
        const maxFileSize = 1024 * 1024 * 1024;
        const uploaderInstance = await UploadService.getInstance(csrfToken);

        if (file.size > maxFileSize) {
            store.dispatch(handleError(ErrorTypes.FILE_TOO_LARGE));
            return undefined;
        }
        if (!fileUploadService.verifyFileType(file)) {
            store.dispatch(handleError(ErrorTypes.FILE_EXTENSION_NOT_ALLOWED));
            return undefined;
        }

        store.dispatch(handleFileUpload(file, new Date(), ticket.ticket));
        return uploaderInstance.uploadFile(
            ticket.key,
            file,
            ticket.ticket,
            (type: string, file: File, payload: any, id: any) => {
                fileUploadService.handleCallback(type, file, payload, id);
            },
        );
    },
    cancelUpload: async (ticket: TicketData, csrfToken: string) => {
        const uploaderInstance = await UploadService.getInstance(csrfToken);

        return uploaderInstance.cancelUpload(ticket.key);
    },
};
